import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import { FLIGHT_OPTIONS_STATUS } from "app/constants";
import QuotationLuggage from "app/pages/Booking/Quotation/Luggage/QuotationLuggage";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import {
	getMaxLuggageCount,
	getSelectedTransportType,
	getFlightOptionsOfSelectedFlight,
} from "app/pages/Booking/bookingSelectors";
import { luggageShape } from "app/utils/propTypes";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { useFormikContext } from "formik";

const QuotationLuggageContainer = ({
	disableFlightSelection = false,
	updateBooking,
	flightBagageOptions = [],
	hasInfant = false,
	selectedTransportType,
	luggageMaxCount = 0,
	flightBagageOption = {},
	flightOptionsStatus,
	flightOptionsFlightCode,
}) => {
	const shouldDisplayLuggages = flightOptionsStatus !== FLIGHT_OPTIONS_STATUS.ERROR;
	const displayFlightPriceInfoBubble = flightOptionsFlightCode
		?.toLowerCase()
		.startsWith("transavia");

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	const { values } = useFormikContext();

	const selectedCheckedBaggageOption = flightBagageOptions.find(
		flightBagageOption =>
			flightBagageOption.quantity === values?.luggage?.quantity &&
			flightBagageOption.maxWeightPerBaggage === values?.luggage?.maxWeightPerBaggage
	);

	const selectedCabinBaggage = flightBagageOption.cabinBaggages?.find(
		cabinBaggage =>
			cabinBaggage.quantity === values?.cabinLuggage?.quantity &&
			cabinBaggage.code === values?.cabinLuggage?.code
	);

	useEffect(() => {
		if (isElementOnScreen && shouldDisplayLuggages) {
			track("quote_view_luggage");
		}
	}, [isElementOnScreen, shouldDisplayLuggages]);

	const onLuggageChange = useCallback(value => {
		updateBooking({
			luggage: {
				quantity: value?.quantity,
				maxWeightPerBaggage: value?.maxWeightPerBaggage,
			},
		});

		track("quote_select_luggage", {
			luggageQuantity: value?.quantity,
			luggagePrice: value?.price,
			luggagePublicPrice: value?.publicPrice,
			luggageMaxWeightPerBaggage: value?.maxWeightPerBaggage,
		});
	}, []);

	const onCabinLuggageChange = useCallback(
		value =>
			updateBooking({
				cabinLuggage: {
					code: value?.code,
					quantity: value?.quantity,
					maxWeightPerBaggage: value?.maxWeightPerBaggage,
				},
			}),
		[]
	);

	return (
		shouldDisplayLuggages && (
			<div ref={ref}>
				<FormattedMessage
					id={
						disableFlightSelection
							? "quotation.stopover.luggage.title"
							: "quotation.luggage.title"
					}
				>
					{message => (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="quotation__subtitle"
							component="h3"
						>
							{message}
						</Typography>
					)}
				</FormattedMessage>
				<div className="quotation__content">
					<QuotationLuggage
						onLuggageChange={onLuggageChange}
						onCabinLuggageChange={onCabinLuggageChange}
						flightBagageOptions={flightBagageOptions}
						displayFlightPriceInfoBubble={displayFlightPriceInfoBubble}
						hasInfant={hasInfant}
						selectedTransportType={selectedTransportType}
						luggageMaxCount={luggageMaxCount}
						flightBagageOption={flightBagageOption}
						selectedCabinBaggage={selectedCabinBaggage}
						selectedCheckedBaggageOption={selectedCheckedBaggageOption}
						flightOptionsStatus={flightOptionsStatus}
					/>
				</div>
			</div>
		)
	);
};

QuotationLuggageContainer.propTypes = {
	updateBooking: PropTypes.func,
	flightBagageOptions: PropTypes.array,
	flightOptionsFlightCode: PropTypes.string,
	disableFlightSelection: PropTypes.bool,
	hasInfant: PropTypes.bool,
	luggageMaxCount: PropTypes.number,
	flightBagageOption: luggageShape,
	selectedTransportType: PropTypes.string,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
};

const mapStateToProps = state => {
	return {
		flightBagageOption: getFlightOptionsOfSelectedFlight(state)?.flightBagageOption,
		flightBagageOptions: getFlightOptionsOfSelectedFlight(state)?.flightBagageOptions,
		flightOptionsStatus: state.quotation.flightOptionsStatus,
		hasInfant: state.booking.infants > 0,
		selectedTransportType: getSelectedTransportType(state),
		luggageMaxCount: getMaxLuggageCount(state),
		flightOptionsFlightCode: state?.quotation?.flightOptionsFlightCode,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(QuotationLuggageContainer);
